import React, { useState, useEffect } from 'react';
import { CenterContent } from 'global/components/perform-grid';
import { Icon, Button, Link, Snackbar, Checkbox, FormControlLabel } from '@mui/material';
import { get, size, set, take, last } from 'global/lib/lodash';
import clsx from 'clsx';
import NotifyModal from './NotifyModal';
import { useSelector, useDispatch } from 'react-redux';
import ShareModal from './ShareModal';
import { ignoreNewTasks, undoIgnored, setNewTasksToSent } from 'redux/actions/view';
import { Alert } from 'global/components/alerts';
import SnackAlerts from 'global/components/snack-alerts';
import { useScreen, Loader } from 'global/styles';
import makeStyles from './TaskNotify.styles';
import { _size } from 'global/utils';
import { useUser } from 'global/hooks/useUser';
import { IconButton } from '../../../global/components/Button';
import colors from '../../../global/styles/colors';
import { undoSent } from '../../../redux/actions/view';

const useUnsentTasks = () => {
  const [loading] = useState(false);
  const [data, setData] = useState([]);
  const [name, setName] = useState({ str: '', userGroups: {} });
  const [selName, setSelName] = useState({ str: '', userGroups: {} });
  const newTasks = useSelector((state) => get(state, 'view.task.new'));
  const { me } = useUser();
  const selectedTasks = newTasks.filter((t) => t.fromSelected);

  const buildName = (tasks) => {
    const names = [];
    const users = {};
    const userGroups = {};

    (tasks || []).map((task) => {
      const str = [];
      const assignees = get(task, 'assignees', []);
      const group = assignees
        .sort((a, b) => parseInt(get(a, 'id')) - parseInt(get(b, 'id')))
        .map((a) => {
          set(users, a.id, a);
          str.push(a.name);
          return a.id;
        })
        .join('_');

      set(userGroups, group, {
        assignees,
        tasks: [...get(userGroups, [group, 'tasks'], []), task],
      });
    });

    const limit = 3;
    for (let k in users) {
      const user = users[k];
      if (k == me.id) {
        names.push('you');
      } else {
        names.push(user.name);
      }
    }

    const others = size(names) > limit ? `${size(names) - limit} other${size(names) - limit > 1 ? 's' : ''}` : null;
    let str = take(names, limit).join(', ') + (others ? ` and ${others}` : '');
    return {
      str,
      userGroups,
    };
  };

  useEffect(() => {
    setData(newTasks);
    setName(buildName(newTasks));
    setSelName(buildName(selectedTasks));
  }, [newTasks]);

  return {
    data,
    selectedTasks,
    name,
    selName,
    loading,
    newTasks,
  };
};

// eslint-disable-next-line import/no-anonymous-default-export
export default (props) => {
  const [showModal, setShowModal] = useState(false);
  const [shareModal, setShareModal] = useState({ open: false });
  const [sent, setSent] = useState();
  const [sending, setSending] = useState(false);
  const [alerts, setAlerts] = useState();
  const [shareLoading, setShareLoading] = useState(false);
  const dispatch = useDispatch();
  const { isMobileXS } = useScreen();
  const classes = makeStyles({ isMobile: isMobileXS });
  const [state, setState] = useState({
    emailCopy: true,
    seperateByTrade: true,
    taskString: ''
  })

  const unsentTasks = useUnsentTasks();
  const [selCount, setSelCount] = useState(size(unsentTasks.selectedTasks));

  useEffect(() => {
    const newSize = size(unsentTasks.selectedTasks);
    if (newSize === selCount) return;

    setSelCount(newSize);
    if (newSize > 0) {
      setAlerts({
        message: `${size(unsentTasks.selectedTasks)} item(s) selected`,
      });
    }
  }, [unsentTasks.selectedTasks]);

  useEffect(() => {
    if (size(unsentTasks.newTasks)) {
      const taskString = unsentTasks.newTasks.map(t => t.id).toString();
      setState(s => ({ ...s, taskString }))

      if (taskString !== state.taskString) {
        setShareModal(sm => ({ ...sm, hasDisplayed: false }));
      }
    }
  }, [unsentTasks.newTasks]);

  const isDisplayed = !!size(unsentTasks.data);

  const clickModify = () => {
    setShowModal(true);
  };

  const setAlert = (opts) => {
    setSent({
      showSnack: true,
      ...opts,
    });
  };

  const dontNotify = (arr, isFromShareClose = false) => {
    dispatch(ignoreNewTasks(arr));
    setSent({
      showSnack: true,
      message: isFromShareClose || shareModal.hasDisplayed ? 'Notify menu closed' : `No notice sent for ${size(arr || unsentTasks.data)} tasks`,
      action: (
        <Button
          startIcon={<Icon>undo</Icon>}
          onClick={() => {
            dispatch(undoIgnored());
            setSent();
          }}
          className={classes.actionButton}
          variant="contained"
        >
          {isFromShareClose || shareModal.hasDisplayed ? 'RE-OPEN' : `UNDO`}
        </Button>
      )
    });
  };

  const share = async (tasks) => {
    try {
      if (shareLoading) {
        return;
      }

      const _tasks = tasks || unsentTasks.data || [];
      setShareModal({
        ...state,
        open: true,
        tasks: _tasks,
        hasDisplayed: true,
        onClose: () => {
          dontNotify(undefined, true);
          setShareModal(s => ({ ...s, open: false }));
        }
      });
    } catch (err) {
      setShareLoading(false);
    }
  };

  const notify = async (opts = {}) => {
    try {
      if (sending) {
        return;
      }
      setSending(true);
      const messages = await dispatch(setNewTasksToSent(null, state));
      setSent({
        showSnack: true,
        message: `${size(messages)} email(s) of ${size(unsentTasks.data)} tasks sent to ${get(unsentTasks, 'name.str')}!`,
        action: (
          <Button
            startIcon={<Icon>undo</Icon>}
            onClick={() => {
              dispatch(undoSent());
              setSent();
            }}
            className={classes.actionButton}
            variant="contained"
          >
            {'RE-OPEN'}
          </Button>
        )
      });
      setShowModal();
      setSending(false);
    } catch (err) {
      setSending(false);
    }
  };

  const isJustSelected = () => {
    const { selectedTasks, data } = unsentTasks;
    return size(selectedTasks) === size(data);
  };

  const getDisplayText = () => {
    const { data, selectedTasks, name, selName } = unsentTasks;
    const gsize = _size(name.userGroups);
    const isOneUser = gsize === 1 && Object.keys(name.userGroups)[0].indexOf('_') === -1;
    const hasSel = !!selectedTasks.find(t => t.fromSelected);

    if (isOneUser) {
      return `${size(data)} new task(s) have been ${hasSel ? 'selected' : 'created'}, assigned to ${get(name, 'str')}. `;
    } else if (isJustSelected()) {
      return `${size(data)} tasks are selected, assigned to ${get(selName, 'str')}. `;
    } else if (size(selectedTasks)) {
      return `You've created ${size(data) - size(selectedTasks)} new tasks, assigned to ${get(name, 'str')} and ${size(selectedTasks)} tasks are selected, assigned to ${get(selName, 'str')}. `;
    }

    return `You've created ${size(data)} new tasks, assigned to ${_size(name.userGroups)} groups of users, including ${get(name, 'str')}. `;
  };

  const notifyButtonText = () => {
    if (isJustSelected()) {
      return 'Deselect';
    }
    return isMobileXS ? 'No' : `Don't Notify`;
  };

  const isListOption = get(props, 'allIsSelected') !== undefined;

  return <>
    <div className={clsx(classes.container, isDisplayed && 'visible', isListOption && 'isListOption')}>
      {isDisplayed && <CenterContent fullWidth={true}>
        <div className={classes.row}>
          <div className={classes.iconCol}>
            {!isListOption && <IconButton
              variant="default"
              icon={<Icon style={{ color: 'black' }}>close</Icon>}
              onClick={() => dontNotify()}
              size="large" />}
            {isListOption && <Checkbox indeterminate={!props.allIsSelected} checked={!!props.allIsSelected} onClick={() => dontNotify()} />}
          </div>

          <div className={classes.contentCol}>
            <div className={classes.textWrapper}>
              <div className={classes.displayText}>
                <span>{getDisplayText()}<b>&nbsp;{'How do you want to share them?'}</b></span>
                <Link className={classes.linkButton} onClick={clickModify}>
                  <i>Modify Details</i>
                </Link>
              </div>
            </div>
          </div>
        </div>
        <div className={clsx(classes.row, classes.buttonContainer)}>
         <FormControlLabel label={isMobileXS ? "Copy me" : "Email me a copy"} control={<Checkbox color={"default"} checked={state.emailCopy} onChange={e => setState(s => ({ ...s, emailCopy: !s.emailCopy }))} />} />
         <FormControlLabel label={isMobileXS ? "Separate by Trade" : "Separate items by Trade"} control={<Checkbox color={"default"} checked={state.seperateByTrade} onChange={e => setState(s => ({ ...s, seperateByTrade: !s.seperateByTrade }))} />} />
        </div>
        <div className={clsx(classes.row, classes.buttonContainer)}>
          <Button
            startIcon={sending ? <Loader size={16} /> : <Icon>mail</Icon>}
            onClick={notify}
            className={clsx(classes.button, classes.notify)}
            variant="contained"
            disabled={shareLoading}
          >
            {`EMAIL ASSIGNEES`}
          </Button>

          <Button startIcon={shareLoading ? <Loader size={16} /> : <Icon>link</Icon>} onClick={() => share()} className={clsx(classes.button, classes.share)} variant="contained">
            {`SHARE LINKS`}
          </Button>
        </div>
      </CenterContent>}
    </div>

    <NotifyModal
      open={showModal && !!get(unsentTasks, 'data')}
      onClose={() => setShowModal(false)}
      unsentTasks={unsentTasks}
      actions={{
        notify,
        share,
        clickModify,
        dontNotify,
        setAlert,
      }}
      {...props}
    />

    <ShareModal
      {...shareModal}
      actions={{
        share,
      }}
    />

    <Snackbar anchorOrigin={{ vertical: 'top', horizontal: 'center' }} open={get(sent, 'showSnack')} autoHideDuration={10000} onClose={() => setSent({ ...sent, showSnack: false })}>
      <Alert severity={get(sent, 'severity', 'success')} action={get(sent, 'action')} icon={get(sent, 'icon', <Icon>double_arrow</Icon>)}>
        <div className={classes.sentMessageText}>{get(sent, 'message', '')}</div>
      </Alert>
    </Snackbar>

    <SnackAlerts alerts={alerts} setAlerts={setAlerts} />
  </>;
};
